import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import type { RiderBuild } from "../styles";
import { inked } from "../toon";
import type { SkaterLook } from "./look";
import { bottomsCloth, cloth, skinMat } from "./materials";
import { buildBandana, buildBelt, buildTopDetails } from "./outfit";
import { buildJorts } from "./denim-natural";
import { buildNeckerchief, buildShapedBelt, buildSpaceSuit, buildTankTop, buildWesternShirt } from "./outfit-natural";
import { buildPelvis, buildTrunk, trunkFront } from "./trunk";
import { facets } from "./geom";

// The rider's middle, one entry per torso kind (`RiderBuild.torso`, #114):
// the pelvis, the chest, and what's worn on them. `buildRider` calls each
// part at its place in the rig's fixed child order.

/** One torso kind. `width` is the rider's (0.9 for a narrow build, else 1). */
export interface TorsoKind {
  /** The pelvis, on the hips. */
  pelvis(look: SkaterLook, width: number): THREE.Mesh;
  /** The chest, on the torso. */
  chest(look: SkaterLook, width: number, build: RiderBuild): THREE.Mesh;
  /** Extras after the rig's own children, so the joints keep their child indexes: top details, bandana, belt. */
  extras(look: SkaterLook, torso: THREE.Group, hips: THREE.Group, width: number): void;
}

export const TORSOS: Readonly<Record<RiderBuild["torso"], TorsoKind>> = {
  // The toy: a rounded-box pelvis and a capsule chest.
  capsule: {
    pelvis(look, width) {
      // Cut-offs sit lower and baggier than pants, so the waist is a bit bigger.
      const geo =
        look.bottoms.style === "cutoffs"
          ? new RoundedBoxGeometry(0.24, 0.2, 0.36, facets(2, 1), 0.07)
          : new RoundedBoxGeometry(0.2, 0.16, 0.3, facets(2, 1), 0.06);
      if (width !== 1) geo.scale(1, 1, width);
      return inked(geo, bottomsCloth(look.bottoms));
    },
    chest(look, width, build) {
      const geo = new THREE.CapsuleGeometry(0.15, 0.22, facets(8, 1), facets(20));
      geo.translate(0, 0.24, 0);
      geo.scale(0.9 * (width === 1 ? 1 : 0.95) * build.chest.depth, build.chest.height, 1.15 * width);
      return inked(geo, cloth(look.top.color));
    },
    extras(look, torso, hips, width) {
      buildTopDetails(look, torso, width === 1 ? 1 : 0.95);
      if (look.bandana) buildBandana(look.bandana, torso);
      if (look.bottoms.buckle) buildBelt(look.bottoms, hips);
    },
  },
  // #75's realistic rider (`trunk.ts`): a waist, shoulders out to the arms,
  // a pelvis flaring over the thighs; what's worn lies on them (`outfit-natural.ts`).
  shaped: {
    // One shaped pelvis from inside the trunk down between the legs, flaring over the thighs' tops.
    pelvis: (look, width) => buildPelvis(look.bottoms, width),
    // Under a tank top the trunk is skin, and the top is cut from its shape.
    chest: (look, width) => buildTrunk(look.top.style === "tank" ? skinMat(look.skin) : cloth(look.top.color), width),
    extras(look, torso, hips, width) {
      if (look.top.style === "tank") buildTankTop(look.top, torso, width);
      const chest = (y: number, z: number) => trunkFront(y, z, width);
      if (look.top.style === "western") buildWesternShirt(look.top, torso, chest);
      else if (look.top.style === "spacesuit") buildSpaceSuit(look.top, torso, chest);
      if (look.bandana) buildNeckerchief(look.bandana, torso, chest);
      if (look.bottoms.style === "cutoffs") buildJorts(look.bottoms, hips, width);
      if (look.bottoms.buckle) buildShapedBelt(look.bottoms, hips, width);
    },
  },
};
